import { useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { GenericCard } from '@/components/molecules/GenericCard';
import { Loader } from '@/components/atoms/Loader';
import { getCdnVideos, type CdnVideo } from '@/services/videos';

const CDN_VIDEOS_BASE = `${import.meta.env.VITE_CDN_URL}/videos`;

const videoTitle = (name: string) =>
  name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');

const videoThumbnailSrc = (name: string) =>
  `${CDN_VIDEOS_BASE}/thumbnails/${name.replace(/\.[^.]+$/, '')}.jpg`;

const VideoThumbnailImg = ({
  videoSrc,
  name,
  className,
}: {
  videoSrc: string;
  name: string;
  className?: string;
}) => {
  const [cdnFailed, setCdnFailed] = useState(false);
  const vidRef = useRef<HTMLVideoElement>(null);

  const handleSeekToMiddle = () => {
    const vid = vidRef.current;
    if (!vid || !vid.duration) return;
    vid.currentTime = vid.duration * 0.5;
  };

  if (cdnFailed) {
    return (
      <video
        ref={vidRef}
        src={videoSrc}
        preload="metadata"
        muted
        playsInline
        onLoadedMetadata={handleSeekToMiddle}
        className={className}
      />
    );
  }

  return (
    <img
      src={videoThumbnailSrc(name)}
      alt={videoTitle(name)}
      onError={() => setCdnFailed(true)}
      className={className}
    />
  );
};

const VideoListItem = ({
  video,
  index,
  isActive,
  onClick,
}: {
  video: CdnVideo;
  index: number;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`group flex w-full items-center gap-3 rounded-xl p-2 text-left transition-all duration-150 ${
      isActive ? 'bg-primary-500/10 ring-1 ring-primary-500/30' : 'hover:bg-black/5'
    }`}
  >
    <div className="relative aspect-video w-20 flex-shrink-0 overflow-hidden rounded-lg bg-black">
      <VideoThumbnailImg
        videoSrc={`${CDN_VIDEOS_BASE}/${video.name}`}
        name={video.name}
        className="h-full w-full object-cover transition-opacity duration-150 group-hover:opacity-80"
      />
      {isActive && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Play size={12} className="ml-0.5 text-white" fill="white" />
        </div>
      )}
    </div>
    <div className="min-w-0 flex-1">
      <p className={`font-lexend text-xs font-semibold capitalize leading-tight ${isActive ? 'text-primary-500' : 'text-content'}`}>
        {videoTitle(video.name)}
      </p>
      <p className="mt-0.5 text-[10px] text-content/40">#{index + 1}</p>
    </div>
    {video.name.toLowerCase().endsWith('.mov') && (
      <span className="flex-shrink-0 rounded bg-yellow-400/20 px-1.5 py-0.5 text-[9px] font-semibold text-yellow-600">MOV</span>
    )}
  </button>
);

export const Videos = () => {
  const { data: videos = [], isLoading, isError } = useQuery({
    queryKey: ['cdn-videos'],
    queryFn: getCdnVideos,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const playerRef = useRef<HTMLVideoElement>(null);

  const active = videos[activeIndex] ?? null;
  const activeSrc = active ? `${CDN_VIDEOS_BASE}/${active.name}` : null;

  const handleSelect = (index: number) => {
    setActiveIndex(index);
    setTimeout(() => playerRef.current?.play(), 50);
  };

  return (
    <main className="min-h-screen">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-5">
        <GenericCard title="Vidéos" subtitle="Les vidéos du bitume">
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="min-w-0 flex-1">
              {active && (
                <div className="mb-2 px-0.5">
                  <h2 className="text-content font-lexend text-base font-semibold capitalize leading-tight">
                    {videoTitle(active.name)}
                  </h2>
                </div>
              )}
              <div className="overflow-hidden rounded-2xl bg-black shadow-xl">
                {isLoading ? (
                  <div className="flex aspect-video w-full items-center justify-center">
                    <Loader />
                  </div>
                ) : isError ? (
                  <div className="flex aspect-video w-full items-center justify-center">
                    <p className="text-sm text-white/40">Impossible de charger les vidéos</p>
                  </div>
                ) : activeSrc ? (
                  <div className="relative aspect-video w-full">
                    <video
                      ref={playerRef}
                      key={activeSrc}
                      src={activeSrc}
                      poster={videoThumbnailSrc(active.name)}
                      controls
                      playsInline
                      preload="metadata"
                      className="h-full w-full"
                    />
                  </div>
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center">
                    <p className="text-sm text-white/40">Aucune vidéo disponible</p>
                  </div>
                )}
              </div>
            </div>

            {videos.length > 1 && (
              <div className="lg:w-56 lg:shrink-0">
                <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-content/40">
                  {videos.length} vidéos
                </p>
                <div className="flex flex-col gap-0.5 lg:max-h-[380px] lg:overflow-y-auto">
                  {videos.map((video, i) => (
                    <VideoListItem
                      key={video.name}
                      video={video}
                      index={i}
                      isActive={activeIndex === i}
                      onClick={() => handleSelect(i)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </GenericCard>
      </div>
    </main>
  );
};